import React, { useState, useEffect } from "react";
import Pricing from "./Pricing";
import { MainContainer, CompanyInfoContainer, InfoText, InfoImage } from "./styled";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchData,
  selectIsLoading,
  selectActiveSubscription,
  selectUser,
  fetchActiveSubscription,
} from "../../../mainApp/features/DataSlice/dataSlice";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import LoadingPage from "../TechnicalTabs/LoadingPage";
import ErrorPage from "../TechnicalTabs/ErrorPage";
import { handleLoginAccess } from "../Utility/handleLoginAccess";
import StripeCheckoutButton from "../Stripe/stripe";
import { toast } from "react-toastify";
import companyImage from '../Assets/company.png';

// Price IDs z Dashboarda Stripe
// TODO: Zweryfikuj priceId dla planów rocznych w panelu Stripe
const PRICE_IDS = {
  standardMonthly: process.env.REACT_APP_STANDARD_MONTHLY,
  premiumMonthly: process.env.REACT_APP_PREMIUM_MONTHLY,
  standardYearly: process.env.REACT_APP_STANDARD_YEARLY,
  premiumYearly: process.env.REACT_APP_PREMIUM_YEARLY,
};

const StyledTitle = styled.h2`
  margin-top: 30px;
  font-size: 24px;
  font-weight: 700;
  padding: 16px 0;
  color: #ffffff;
  margin-bottom: 24px;
  letter-spacing: -0.5px;
  position: relative;
  text-align: center;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: 8px;
    left: 45.65%;
    transform: translateX(-50%);
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, #1db954 0%, rgba(29, 185, 84, 0) 100%);
    border-radius: 3px;
  }
`;

const TabButton = styled.button`
  padding: 8px 16px;
  width: 110px;
  background-color: #282828;
  color: #ffffff;
  border: none;
  cursor: pointer;
  font-weight: 400;
  border-radius: ${({ left }) => (left ? '4px 0 0 4px' : '0 4px 4px 0')};
  transition: background-color 0.3s ease, color 0.3s ease;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;

  &.active {
    background-color: #179544 !important;
    color: #000000 !important;
    font-weight: 600 !important;
  }
`;

const PricingPlans = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [initialLoading, setInitialLoading] = useState(true);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const isLoading = useSelector(selectIsLoading);
  const activeSubscription = useSelector(selectActiveSubscription);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);

  const handleManageSubscription = async () => {
    if (!user) {
      toast.error("Musisz być zalogowany, aby zarządzać subskrypcją.");
      return;
    }

    try {
      const response = await fetch(
        "/.netlify/functions/create-customer-portal-session",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId: user.uid }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Nie udało się otworzyć portalu subskrypcji."
        );
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Błąd przy tworzeniu sesji portalu klienta:", error);
      toast.error(error.message);
    }
  };

  // Funkcja opóźnienia
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Sprawdź autoryzację i pobierz dane użytkownika
  useEffect(() => {
    dispatch(fetchData());
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            setUserDetails(snap.data());
          } else {
            console.warn("Dokument użytkownika nie istnieje.");
            setUserDetails(null);
          }
          setIsUserLoggedIn(true);
          await delay(600); // Dodatkowe opóźnienie jako zabezpieczenie
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          setUserDetails(null);
          setIsUserLoggedIn(true); // Użytkownik jest zalogowany, mimo błędu Firestore
          await delay(600); // Opóźnienie w przypadku błędu
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  const getPlanKey = (plan) => {
    const key = plan + (billingCycle === "monthly" ? "Monthly" : "Yearly");
    return key;
  };

  const standardData = [
    { text: 'Nielimitowany dostęp Standard', value: true },
    { text: 'Asystent prawny', value: true },
    { text: 'Reżyser Wideo', value: true },
    { text: 'Asystent Diety', value: true },
    { text: 'Kreator Postów', value: true },
  ];

  const premiumData = [
    { text: 'Wszystko to co w Standard', value: true },
    { text: 'Nielimitowany dostęp Premium', value: true },
    { text: 'Wyszukiwarka AI', value: true },
    { text: 'Kreator Obrazów', value: true },
    { text: 'Generator Wideo', value: true },
    { text: 'Kompozytor Muzyczny', value: true },
    { text: 'Priorytet w otrzymywaniu nowości', value: true },
  ];

  const TabContainer = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        margin: '0 auto 20px auto',
        maxWidth: '250px',
      }}
    >
      <TabButton
        className={billingCycle === 'monthly' ? 'active' : ''}
        left
        onClick={() => setBillingCycle('monthly')}
      >
        Miesięcznie
      </TabButton>
      <TabButton
        className={billingCycle === 'yearly' ? 'active' : ''}
        onClick={() => setBillingCycle('yearly')}
      >
        Rocznie
      </TabButton>
    </div>
  );

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;

  switch (isLoading) {
    case "success":
      if (!isUserLoggedIn) {
        returned = handleLoginAccess();
        
      } else {
        returned = (
          <>
            <StyledTitle>Plany i ceny</StyledTitle>
            <TabContainer />
            <MainContainer>
              <div className="pricing-component">
                <Pricing
                  data={standardData}
                  price={billingCycle === "monthly" ? 99 : 999}
                  currency="PLN"
                  buttonContent={
                    <StripeCheckoutButton
                      priceId={PRICE_IDS[getPlanKey("standard")]}
                      isCurrent={
                        activeSubscription?.priceId ===
                        PRICE_IDS[getPlanKey("standard")]
                      }
                      isFuture={
                        activeSubscription?.scheduledChange?.newPriceId ===
                        PRICE_IDS[getPlanKey("standard")]
                      }
                      hasActiveSubscription={!!activeSubscription}
                      onManageSubscription={handleManageSubscription}
                    />
                  }
                  subTitle="Świetne na początek"
                  priceText={`${billingCycle === 'monthly' ? '1188zł' : '83,25zł'} ${
                    billingCycle === 'monthly' ? '/ rok' : '/ mies.'
                  }`}
                  headerText="standard"
                  background="#282828"
                  shadow="rgba(0, 0, 0, 0.5)"
                />
              </div>
              <div className="pricing-component">
                <Pricing
                  data={premiumData}
                  price={billingCycle === "monthly" ? 199 : 1999}
                  currency="PLN"
                  buttonContent={
                    <StripeCheckoutButton
                      priceId={PRICE_IDS[getPlanKey("premium")]}
                      isCurrent={
                        activeSubscription?.priceId ===
                        PRICE_IDS[getPlanKey("premium")]
                      }
                      isFuture={
                        activeSubscription?.scheduledChange?.newPriceId ===
                        PRICE_IDS[getPlanKey("premium")]
                      }
                      hasActiveSubscription={!!activeSubscription}
                      onManageSubscription={handleManageSubscription}
                    />
                  }
                  subTitle="Pełna moc"
                  priceText={`${billingCycle === 'monthly' ? '2388zł' : '166,60zł'} ${
                    billingCycle === 'monthly' ? '/ rok' : '/ mies.'
                  }`}
                  headerText="premium"
                  background="linear-gradient(90deg, #ffdb4d 0%, #ff8c00 100%)"
                  shadow="rgba(0, 0, 0, 0.5)"
                />
              </div>
            </MainContainer>
            <CompanyInfoContainer>
              <InfoText>
                <strong>Uwaga!</strong> Podane ceny są cenami <strong>brutto</strong>. Prowadzisz firmę? Jesteś płatnikiem VAT? Nie zapomnij zaznaczyć okienka "Kupuję jako firma", oraz wpisać swojego numeru NIP.
              </InfoText>
              <InfoImage src={companyImage} alt="Instrukcja dla firm" />
            </CompanyInfoContainer>
          </>
        );
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  return returned;
};

export default PricingPlans;